import get from 'lodash/get';
import config from '../config';
import pickBy from 'lodash/pickBy';
import identity from 'lodash/identity';
import {
  FILTER_KEY_AIRFRAME_TIME,
  FILTER_KEY_CATEGORY,
  FILTER_KEY_HAS_ACCIDENT_HISTORY,
  FILTER_KEY_HOURS_FLOWN,
  FILTER_KEY_MAKER,
  FILTER_KEY_MODEL,
  FILTER_KEY_MODEL_FAMILY,
  FILTER_KEY_PRICE,
  FILTER_KEY_PRICE_DECREASE,
  FILTER_KEY_PRICE_INCREASE,
  FILTER_KEY_SEATS,
  FILTER_KEY_YEAR,
  FILTER_KEYS_ENGINE_HOURS,
} from '../components/FilterComponent/FilterComponent.constants';
import { validURLParamsForExtendedData } from '../components/FilterComponent/FilterComponent.helpers';
import {
  getMakerAndModelAndCategoryForSearchFormByCanNames,
  getMakerAndModelAndCategoryForSearchFormByIds,
} from '../ducks/AirplanesData.duck';

import {
  getExclusiveEndDate,
  parseDateFromISO8601,
} from './dates';
import {
  priceSearchParams,
} from './currency';
import {
  encodeLatLngBounds,
  parse,
} from './urlHelpers';

/**
 * SelectMultipleFilter needs to parse values from format
 * "has_all:a,b,c,d" or "a,b,c,d"
 */
export const parseSelectFilterOptions = uriComponentValue => {
  const startsWithHasAll = uriComponentValue && uriComponentValue.indexOf('has_all:') === 0;
  const startsWithHasAny = uriComponentValue && uriComponentValue.indexOf('has_any:') === 0;

  if (startsWithHasAll) {
    return uriComponentValue.substring(8).split(',');
  } else if (startsWithHasAny) {
    return uriComponentValue.substring(8).split(',');
  } else {
    return uriComponentValue.split(',');
  }
};

/**
 * Check if any of the filters (defined by filterIds) have currently active query parameter in URL.
 */
export const isAnyFilterActive = (filterIds, urlQueryParams, filterConfigs) => {
  const getQueryParamKeysOfGivenFilters = (keys, config) => {
    const isFilterIncluded = filterIds.includes(config.id);
    const addedQueryParamNamesMaybe = isFilterIncluded ? config.queryParamNames : [];
    return [...keys, ...addedQueryParamNamesMaybe];
  };
  const queryParamKeysOfGivenFilters = filterConfigs.reduce(getQueryParamKeysOfGivenFilters, []);

  const paramEntries = Object.entries(urlQueryParams);
  const activeKey = paramEntries.find(entry => {
    const [key, value] = entry;
    return queryParamKeysOfGivenFilters.includes(key) && value != null;
  });
  return !!activeKey;
};

/**
 * Check if the filter is currently active.
 */
export const findOptionsForSelectFilter = (filterId, filters) => {
  const filter = filters.find(f => f.id === filterId);
  return filter && filter.config && filter.config.options ? filter.config.options : [];
};

/**
 * Return filter config
 */
export const findConfigForSelectFilter = (filterId, filters) => {
  const filter = filters.find(f => f.id === filterId);
  return filter && filter.config ? filter.config : null;
};

/**
 * Check if the main search type is 'keywords'
 */
export const isMainSearchTypeKeywords = config => config.mainSearchType === 'pub_keywords';

/**
 * Check if the origin parameter is currently active.
 */
export const isOriginInUse = config =>
  config.mainSearchType === 'location' && config.sortSearchByDistance;

/**
 * Check if the stock management is currently active.
 */
export const isStockInUse = config => config.listingManagementType === 'stock';

export const getSearchOptionLabelByKey = (searchOptions, key) => {
  return get(searchOptions.find(i => i.key === key), 'label', null);
};

export const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

export const prepareSearchParams = (urlParams, searchParams) => {
  const datesSearchParams = datesParam => {
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesParam && values.length === 2;
    const startDate = hasValues ? values[0] : null;
    const isNightlyBooking = config.lineItemUnitType === 'line-item/night';
    const endDate =
      hasValues && isNightlyBooking
        ? values[1]
        : hasValues
          ? getExclusiveEndDate(values[1], 'Etc/UTC')
          : null;

    return hasValues
      ? {
        start: parseDateFromISO8601(startDate, 'Etc/UTC'),
        end: parseDateFromISO8601(endDate, 'Etc/UTC'),
        availability: 'full',
      }
      : {};
  };

  const { perPage, price, dates, sort, ...rest } = searchParams;
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const sortMaybe = sort === config.custom.sortConfig.relevanceKey ? {} : { sort };

  Object.keys(rest).forEach(key => {
    if (typeof rest[key] === 'boolean') {
      rest[key] = rest[key].toString();
    }
  });

  const params = {
    ...rest,
    ...priceMaybe,
    ...datesMaybe,
    ...sortMaybe,
    per_page: perPage,
  };

  return pickBy({
    ...urlParams,
    ...{
      ...params,
      ...(params.bounds && { bounds: encodeLatLngBounds(params.bounds) }),
    },
  }, identity);
};

export const hasSearchParamsInUrl = () => {
  const isWindowDefined = typeof window !== 'undefined';
  if (isWindowDefined) {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('page');
    searchParams.delete('createdAtStart');
    searchParams.delete('sort');
    return Array.from(searchParams.keys()).length;
  }
  return false;
}

export const validUrlQueryParamsFromProps = props => {
  const { location, filterConfig } = props;
  // eslint-disable-next-line no-unused-vars
  const { mapSearch, page, ...searchInURL } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  // urlQueryParams doesn't contain page specific url params
  // like mapSearch, page or origin (origin depends on config.sortSearchByDistance)
  return validURLParamsForExtendedData(searchInURL, filterConfig);
};

export const cleanSearchFromConflictingParams = (searchParams, sortConfig, filterConfig) => {
  // Single out filters that should disable SortBy when an active
  // keyword search sorts the listings according to relevance.
  // In those cases, sort parameter should be removed.
  const sortingFiltersActive = isAnyFilterActive(
    sortConfig.conflictingFilters,
    searchParams,
    filterConfig
  );
  return sortingFiltersActive
    ? { ...searchParams, [sortConfig.queryParamName]: null }
    : searchParams;
};


export const rangeSearchParamToRange = param => {
  if (!param) {
    return null;
  }
  if (param.includes(',')) {
    const splitet = param.split(',');
    const min = Number(splitet[0]);
    const max = Number(splitet[1]);
    return `${min},${max - 1}`;
  }
  return `${param},${param}`;
};

export const setInitialFilterValues = (params, search, setSearchValues) => dispatch => {
  search = new URLSearchParams(search);
  dispatch(setSearchValues({
    [FILTER_KEY_YEAR]: rangeSearchParamToRange(search.get(FILTER_KEY_YEAR)),
    [FILTER_KEY_PRICE]: search.get(FILTER_KEY_PRICE),
    [FILTER_KEY_AIRFRAME_TIME]: rangeSearchParamToRange(search.get(FILTER_KEY_AIRFRAME_TIME)),
    ...FILTER_KEYS_ENGINE_HOURS.reduce((acc, curr) => {
      return { ...acc, [curr]: rangeSearchParamToRange(search.get(curr)) };
    }, {}),
    [FILTER_KEY_SEATS]: rangeSearchParamToRange(search.get(FILTER_KEY_SEATS)),
    [FILTER_KEY_PRICE_INCREASE]: rangeSearchParamToRange(search.get(FILTER_KEY_PRICE_INCREASE)),
    [FILTER_KEY_PRICE_DECREASE]: rangeSearchParamToRange(search.get(FILTER_KEY_PRICE_DECREASE)),
    [FILTER_KEY_HOURS_FLOWN]: rangeSearchParamToRange(search.get(FILTER_KEY_HOURS_FLOWN)),
    [FILTER_KEY_HAS_ACCIDENT_HISTORY]: search.get(FILTER_KEY_HAS_ACCIDENT_HISTORY),
  }));
  // set maker, model and category values
  const categoryParam = params.category;
  const categoryQueryId = search.get(FILTER_KEY_CATEGORY);
  const makerQueryId = search.get(FILTER_KEY_MAKER);
  const modelFamilyQueryId = search.get(FILTER_KEY_MODEL_FAMILY);
  const modelQueryId = search.get(FILTER_KEY_MODEL);
  const isSearchByCanNames = !!categoryParam;
  const isSearchByIds = categoryQueryId || makerQueryId || modelFamilyQueryId || modelQueryId;
  let api;
  let apiParams;
  if (isSearchByCanNames) {
    api = getMakerAndModelAndCategoryForSearchFormByCanNames;
    apiParams = [
      categoryParam,
      params.maker,
      params.modelFamily,
      params.model,
    ];
  } else if (isSearchByIds) {
    api = getMakerAndModelAndCategoryForSearchFormByIds;
    apiParams = [
      categoryQueryId,
      makerQueryId,
      modelFamilyQueryId,
      modelQueryId,
    ];
  }
  if (isSearchByIds || isSearchByCanNames) {
    return dispatch(api(...apiParams))
      .then(response => {
        if (!response) {
          return ;
        }
        dispatch(setSearchValues(response));
        return response
      })
      ;
  }
  return Promise.resolve();
};
